/**
 * App-wide registry of in-progress work that must not be destroyed by a reload.
 *
 * WHY THIS EXISTS — on 2026-08-27 a rep recorded a 3m26s meeting debrief, hit
 * "Save debrief", and while the transcription was still in flight clicked
 * "Update now" on the PWA update banner. The service worker reload landed ~300ms
 * after the transcript came back and ~300ms before the POST went out, so the
 * recording and its transcript — which existed only in React state — were both
 * destroyed with no error and no row. The banner is a fixed-position overlay
 * that appears on top of whatever the user is doing, and it gets clicked roughly
 * 100 times a month across the team, including on /quotes/new and
 * /purchase-orders/new.
 *
 * Two things hang off this registry:
 *   1. `PWAUpdatePrompt` hides the update banner while anything is registered,
 *      so the interruption can't be offered mid-task. It reappears on its own
 *      once the work resolves — nothing is lost, the update just waits.
 *   2. This module owns the single `beforeunload` handler, which is the backstop
 *      for every *other* reload path: `lazyWithRetry`'s hardReload, a
 *      controllerchange reload, an accidental refresh, closing the tab.
 *
 * A registry rather than per-component listeners because (2) has to be one
 * listener with one answer — several handlers each calling preventDefault is
 * both redundant and impossible to reason about.
 */

// id -> label. A Map (not a counter) so a leaked registration is debuggable and
// so `unsavedWorkLabels()` can say what is actually pending.
const holders = new Map();
const listeners = new Set();
let nextId = 1;

function handleBeforeUnload(event) {
  if (holders.size === 0) return undefined;
  // The modern spec form plus the legacy `returnValue` assignment: Chrome and
  // Safari still require the latter to actually show the dialog.
  event.preventDefault();
  event.returnValue = '';
  return '';
}

// Attached only while something is registered. Leaving it attached permanently
// would prompt on every navigation away from the app.
function syncBeforeUnloadListener() {
  if (typeof window === 'undefined') return;
  if (holders.size > 0) {
    window.addEventListener('beforeunload', handleBeforeUnload);
  } else {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  }
}

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // A broken subscriber must not stop the others, and must never break the
      // guard itself.
    }
  }
}

/**
 * Declare that unsaved work exists. Returns a release function; call it when the
 * work is saved or discarded. Releasing twice is a no-op.
 *
 * @param {string} label Human-readable, used in diagnostics (e.g. 'an unsaved meeting debrief').
 * @returns {() => void}
 */
export function registerUnsavedWork(label = 'unsaved work') {
  const id = nextId++;
  holders.set(id, label);
  syncBeforeUnloadListener();
  notify();

  let released = false;
  return function release() {
    if (released) return;
    released = true;
    holders.delete(id);
    syncBeforeUnloadListener();
    notify();
  };
}

/** True when any component has registered unsaved work. */
export function hasUnsavedWork() {
  return holders.size > 0;
}

/** Labels of everything currently registered, for diagnostics. */
export function unsavedWorkLabels() {
  return [...holders.values()];
}

/** Subscribe to registry changes. Returns an unsubscribe function. */
export function subscribeToUnsavedWork(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test-only: clear module state between cases. */
export function __resetUnsavedWorkForTests() {
  holders.clear();
  listeners.clear();
  nextId = 1;
  syncBeforeUnloadListener();
}
